import { useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import { Box } from '@material-ui/core';

import { Document } from '@setvi/shared/components/sresource-children-list/components/page-preview';
import { Resource } from '@setvi/shared/interfaces';
import { useElementSize } from 'hooksv2';

import { BASE_PAGE_WIDTH, SCROLL_AREA_PADDING } from '../../constants';
import { usePdfPager, useZoom } from '../../hooks';
import { useStyles } from '../../styles';
import type { PreviewPage } from '../../types';
import { PreviewPager } from '../preview-pager';
import { PreviewToolbar } from '../preview-toolbar';
import { ZoomControls } from '../zoom-controls';

export interface PdfPreviewContentProps {
  /** Fallback document when a page carries no generated pdf url. */
  resource?: Resource;
  /** Entries to page through, see `usePdfPager`. */
  pages: PreviewPage[];
  initialIndex?: number;
  /** Starting page within the initial whole-document entry (e.g. a citation). */
  initialPage?: number;
  /** Right-aligned toolbar content, e.g. an Add to Cart button. */
  actions?: ReactNode;
}

/**
 * Zoomable, pageable PDF preview: the toolbar and the document canvas. The
 * print-order zoom dialog wraps it in `SBase`; the product asset preview page
 * renders it full screen.
 */
export const PdfPreviewContent = ({
  resource,
  pages,
  initialIndex,
  initialPage,
  actions
}: PdfPreviewContentProps) => {
  const classes = useStyles();
  const zoom = useZoom();
  const pager = usePdfPager({
    pages,
    fallbackUrl: resource?.DownloadURLWithSas,
    initialIndex,
    initialPage
  });

  // A page never renders wider than the canvas at 100%, so a phone shows the
  // whole page and zooming in scrolls it.
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const { width: areaWidth } = useElementSize(scrollAreaRef);
  const baseWidth = areaWidth
    ? Math.min(BASE_PAGE_WIDTH, areaWidth - SCROLL_AREA_PADDING * 2)
    : BASE_PAGE_WIDTH;

  const previewDocument = useMemo(
    () =>
      ({
        ...(resource ?? {}),
        DownloadURLWithSas: pager.activeUrl
      }) as Resource,
    [resource, pager.activeUrl]
  );

  return (
    <>
      <PreviewToolbar actions={actions}>
        <ZoomControls {...zoom} />
        {pager.showPager && (
          <>
            <Box className={classes.divider} />
            <PreviewPager
              label={pager.label}
              prevDisabled={pager.prevDisabled}
              nextDisabled={pager.nextDisabled}
              onPrev={pager.goPrev}
              onNext={pager.goNext}
            />
          </>
        )}
      </PreviewToolbar>

      <div className={classes.scrollArea} ref={scrollAreaRef}>
        <Box className={classes.doc}>
          <Document
            document={previewDocument}
            loading={false}
            activePage={pager.activePage}
            pageWidth={Math.round(baseWidth * zoom.zoom)}
            onLoadPages={pager.registerPageCount}
          />
        </Box>
      </div>
    </>
  );
};
